import { useTranslation } from 'react-i18next';
import personAIUrl from '@/assets/jpg/person-ai-tool.jpg';
import luciusLogoWhiteUrl from '@/assets/svg/lucius-logo-white.svg';
import { LuciusLogo } from '@/components/Custom/Logo/LuciusLogo';
import { twMerge } from 'tailwind-merge';
import { LanguageSelect } from '@/components/Custom/Select/SelectWithIcon';

interface AuthLayoutProps {
  illustrationUrl?: string;
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  children: React.ReactNode;
  contentContainerClassName?: string;
}

export const AuthLayout = ({
  illustrationUrl,
  title,
  description,
  children,
  contentContainerClassName,
}: AuthLayoutProps) => {
  const { t } = useTranslation('auth');

  return (
    <div className="flex flex-wrap flex-1 gap-10">
      <div className="w-[50%] flex flex-col px-10 py-[90px] max-3xl:pt-[70px] max-lg:w-full max-md:py-14 max-sm:px-8">
        <div className="flex justify-center">
          <LuciusLogo
            linkTo="/login"
            containerClassName="gap-3"
            logoIconClassName="w-[60px] h-auto"
            logoTextWrapperClassName="mt-2"
            logoTextClassName="w-[100px] h-auto"
          >
            <div className="mt-1.75 text-center text-[8px]">
              <span className="font-thin">{t('poweredBy').toLowerCase()}</span>{' '}
              <span className="font-semibold">trustoo</span>
            </div>
          </LuciusLogo>
        </div>
        <div
          className={twMerge(
            'mt-[60px] flex justify-center max-md:mt-8',
            contentContainerClassName
          )}
        >
          <div className="max-w-[450px] flex flex-1 flex-col max-md:max-w-none">
            <div className="mb-6 text-[30px] leading-[33px] font-semibold max-sm:mb-3.5 max-md:text-[25px] max-md:leading-[28px]">
              {title}
            </div>
            <div className="mb-10 text-[16.5px] font-thin text-gray-700 max-md:mb-7">
              {description}
            </div>
            <div>{children}</div>
            <div className="mt-10 flex items-center">
              <div className="flex flex-1">
                <LanguageSelect containerClassName="w-[150px]" />
              </div>
              <div>
                <a
                  href="https://lucius-ai.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-md underline"
                >
                  {t('common:contactUs')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-[50%] fixed right-0 top-0 h-screen bg-blue-131025 max-lg:static max-lg:hidden">
        <div className="w-full h-full relative">
          <img
            className=" w-full h-full object-center object-cover"
            src={illustrationUrl ?? personAIUrl}
          />
          <div className="w-full absolute bottom-0 left-0 flex flex-wrap gap-10 px-12 py-16 min-3xl:px-20 max-2xl:px-10">
            <div className="flex flex-1 text-white">
              <div className="max-w-[500px]">
                <div className="text-[35px] font-bold leading-[43px] max-2xl:text-[30px] max-2xl:leading-[38px] max-1.5xl:text-[27px] max-1.5xl:leading-[35px] max-2lg:text-[25px] max-2lg:leading-[33px]">
                  {`${t('yourEfficientAndPreciseAssistant')}.`}
                </div>
                <div className="mt-4 text-white text-md leading-5">
                  {`${t('withTheNewIATechnologyOfLucius')}.`}
                </div>
              </div>
            </div>
            <div className="flex justify-end items-center">
              <img
                src={luciusLogoWhiteUrl}
                className="w-[123px] h-auto max-lg:w-[100px] max-2xl:w-[110px] max-1.5xl:w-[90px] max-lg:h-[65px]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
